/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Formik } from 'formik';
import { memo, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { Link } from 'react-router';

import { Box, Button, Typography } from '@mui/material';

import { DefaultLanguage } from 'shared';

import { SimpleNotifyModal } from '../../components/SimpleNotifyModal/SimpleNotifyModal';
import { BasicRoutes } from '../../constants/basicRoutes';
import { Namespaces } from '../../i18n';
import { SuperuserUserApiService } from '../../services/SuperuserUserApiService';
import { ApiClientError } from '../../types/api/ApiClientError';
import { SystemConfiguration } from '../../types/superuser/SystemConfiguration';
import { UpdateSystemConfigurationRequest } from '../../types/superuser/UpdateSystemConfigurationRequest';
import { useApiValidation } from '../../utils/useApiValidation';
import { useDocumentTitle } from '../../utils/useDocumentTitle';
import { EmailTestPage } from '../EmailTestPage/EmailTestPage';
import { getInitialValues } from '../SystemConfigurationPage/SystemConfigurationPage.consts';
import { useSystemConfigurationPageStyles } from '../SystemConfigurationPage/SystemConfigurationPage.styles';
import { SystemConfigurationFormValues } from '../SystemConfigurationPage/SystemConfigurationPage.types';
import { SystemConfigurationPlaceholder } from '../SystemConfigurationPage/SystemConfigurationPlaceholder';
import { EmailConfigurationForm } from './components/EmailConfigurationForm';
import { emailConfigValidationSchema } from './EmailConfigurationPage.consts';

const connector = connect();
type EmailConfigurationPageProps = ConnectedProps<typeof connector>;

const EmailConfigurationPageComponent = memo<EmailConfigurationPageProps>(() => {
  const { t } = useTranslation(Namespaces.Admin);
  const classes = useSystemConfigurationPageStyles();
  useDocumentTitle(t(`${Namespaces.Common}:page_titles.email_configuration`));
  const { hasFieldApiError, getFieldApiError, resetApiError, resetFieldApiError, setApiError } = useApiValidation();

  const [errorModalState, setErrorModalState] = useState({
    isOpen: false,
    error: {},
  });
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isConfigurationLoading, setIsConfigurationLoading] = useState<boolean>(false);
  const [configurationData, setConfigurationData] = useState<SystemConfiguration | undefined>(undefined);
  const [isOpenSuccessModal, setIsOpenSuccessModal] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsConfigurationLoading(true);
        const config = await SuperuserUserApiService.fetchSystemConfiguration();
        setConfigurationData(config.data.payload);
      } catch (e) {
        const error = e as ApiClientError;
        if (error.response) {
          setApiError(error);
          console.log(error.response);
        }
      } finally {
        setIsConfigurationLoading(false);
      }
    };
    void fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialValues = useMemo(() => {
    if (configurationData) {
      return getInitialValues({
        ...configurationData,
        smtpHost: configurationData.smtpHost ?? '',
        smtpPort: `${configurationData.smtpPort ?? ''}`,
        smtpSecure: configurationData.smtpSecure ?? false,
        smtpUser: configurationData.smtpUser ?? '',
        smtpPass: configurationData.smtpPass ?? '',
        enquiryEmailLanguage: configurationData?.enquiryEmailLanguage || DefaultLanguage,
        smtpFromEmail: configurationData.smtpFromEmail ?? null,
        minCpuLimit: configurationData.minCpuLimit ?? undefined,
        maxCpuLimit: configurationData.maxCpuLimit ?? undefined,
        minMemoryLimit: configurationData.minMemoryLimit ?? null,
        maxMemoryLimit: configurationData.maxMemoryLimit ?? null,
        minStorageLimit: configurationData.minStorageLimit ?? null,
        maxStorageLimit: configurationData.maxStorageLimit ?? null,
        emailAddress: configurationData.emailAddress ?? null,
        emailToken: configurationData.emailToken ?? null,
      });
    }
    return getInitialValues();
  }, [configurationData]);

  const submit = async (values: SystemConfigurationFormValues) => {
    resetApiError();
    setIsFetching(true);
    try {
      const data: UpdateSystemConfigurationRequest = {
        smtpHost: values.smtpHost?.trim() || null,
        smtpPort: Number(values.smtpPort) || null,
        smtpSecure: values.useSmtp ? values.smtpSecure : null,
        smtpUser: values.smtpUser?.trim() || null,
        smtpPass: values.smtpPass?.trim() || null,
        enquiryEmails: values.enquiryEmails.trim(),
        enquiryEmailLanguage: values.enquiryEmailLanguage,
        allowServiceCreation: values.allowServiceCreation,
        showInstanceCost: values.showInstanceCost,
        showSupportCost: values.showSupportCost,
        minCpuLimit: Number(values.minCpuLimit) || null,
        maxCpuLimit: Number(values.maxCpuLimit) || null,
        minMemoryLimit: Number(values.minMemoryLimit) || null,
        maxMemoryLimit: Number(values.maxMemoryLimit) || null,
        minStorageLimit: Number(values.minStorageLimit) || null,
        maxStorageLimit: Number(values.maxStorageLimit) || null,
        minReplicasLimit: Number(values.minReplicasLimit),
        maxReplicasLimit: Number(values.maxReplicasLimit),
        currency: values.currency,
        cpuPricePerMonth: Number(values.cpuPricePerMonth) || 0,
        memoryPricePerMonth: Number(values.memoryPricePerMonth) || 0,
        storagePricePerMonth: Number(values.storagePricePerMonth) || 0,
        cpuRequestToLimitMultiplier: Number(values.cpuRequestToLimitMultiplier) || 1,
        smtpFromEmail: values.smtpFromEmail,
        emailAddress: values.emailAddress,
        emailToken: values.emailToken,
        imprint: values.imprint,
      };
      await SuperuserUserApiService.updateSystemConfiguration({ data });
      setIsOpenSuccessModal(true);
    } catch (e) {
      const error = e as ApiClientError;
      if (error.response) {
        const jsonObject = JSON.stringify(error.response.data, null, 2);
        setApiError(error);
        setErrorModalState({
          isOpen: true,
          error: jsonObject,
        });
      }
      console.log(error);
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <div>
      {isConfigurationLoading ? (
        <SystemConfigurationPlaceholder inputs={4} />
      ) : (
        <Box display="flex" flexDirection="column" width="100%" height="100%" gap="24px">
          <Formik<SystemConfigurationFormValues>
            onSubmit={submit}
            initialValues={initialValues}
            validationSchema={emailConfigValidationSchema}
            validateOnChange
            validateOnBlur
          >
            {formikProps => (
              <Box>
                <Typography variant="h2" className={classes.title}>
                  {t('email_configuration.title')}
                </Typography>
                <form onSubmit={formikProps.handleSubmit}>
                  <EmailConfigurationForm
                    {...formikProps}
                    isFetching={isFetching}
                    hasFieldApiError={hasFieldApiError}
                    getFieldApiError={getFieldApiError}
                    resetFieldApiError={resetFieldApiError}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    className={classes.submitBtn}
                    disabled={isFetching || !formikProps.dirty}
                  >
                    {t('buttons.submit')}
                  </Button>
                </form>
                <SimpleNotifyModal
                  open={errorModalState.isOpen}
                  message={
                    errorModalState.error ? (
                      <pre className={classes.modalError}>{errorModalState.error.toString()}</pre>
                    ) : (
                      t(`${Namespaces.Common}:errors.unexpected_error`)
                    )
                  }
                  onOk={() => setErrorModalState({ isOpen: false, error: '' })}
                />
                <SimpleNotifyModal
                  open={isOpenSuccessModal}
                  message={
                    formikProps.values.useSmtp ? (
                      <Trans
                        i18nKey={`${Namespaces.Admin}:system_configuration.change_saved_redirect`}
                        defaults={t('system_configuration.change_saved')}
                        components={[
                          // eslint-disable-next-line react/jsx-key
                          <Link to={BasicRoutes.ADMIN_SEND_EMAIL}>{t('system_configuration.redirect_page')}</Link>,
                        ]}
                      />
                    ) : (
                      t('system_configuration.change_saved')
                    )
                  }
                  onOk={() => setIsOpenSuccessModal(false)}
                />
              </Box>
            )}
          </Formik>
          <EmailTestPage />
        </Box>
      )}
    </div>
  );
});

export const EmailConfigurationPage = connector(EmailConfigurationPageComponent);
